'use client';
import {
  Box,
  Flex,
  Stack,
  Input,
  Button,
  IconButton,
  Icon,
  Text,
  Image,
  VStack,
  Spinner,
  Center,
} from '@chakra-ui/react';
import Link from 'next/link';
import {
  Collection,
  CollectionFilled,
} from '@/components/ui/svgs/TradePage/TokenDetail';
import { Logo } from '../ui/svgs/logo';
import { EmailRegister } from './emailRegister';
import { ColorModeButton, useColorMode } from '../ui/color-mode';
import { DownloadIcon, LanguageIcon, SearchIcon } from '../ui/svgs/navIcons';
import { UserWalletMenu } from './userWalletMenu';
import { useState, useEffect } from 'react';
import { UserHomeMenu } from './userHomeMenu';
import { FaRegTrashAlt, FaStar } from 'react-icons/fa';
import { useTranslations } from 'next-intl';
import { EmailLogin } from './emailLogin';
import { usePathname, useSearchParams } from 'next/navigation';
import { useRouter } from 'next/navigation';
import { clearToken, useSession } from '@/lib/hooks/useSession';
import {
  CHAIN_ID_TO_LOWER_NAME,
  CHAIN_IDS,
  CHAIN_LOWER_NAME_TO_ID,
} from '@/lib/config/chain';
import {
  marketCapValueFormat,
  formatPrice,
  formatPercent,
  abbreviateHashString,
} from '@/lib/utils/number.utils';
import {
  postMarketTokenFavorite,
  TokenFavorite,
  MarketTrending,
  requestMarketSearch,
  useTrendingSearch,
  useMarketSearch,
} from '@/lib/services/token.service';
import { useAppDispatch } from '@/lib/hooks/storeHook';
import { checkChainId } from '@/lib/config/chain';
import { useQueryClient } from '@tanstack/react-query';
import { resetStore } from '@/lib/stores/store';
import { useDefaultTokenAvatar } from '@/lib/hooks/useDefaultTokenAvatar';
import { useLoginRedirect } from '@/lib/hooks/useLoginRedirect';
import { Phone } from '@/components/ui/svgs/Setting/phone';
import { Button as ButtonUI } from '@/components/ui/button';
import nProgress from 'nprogress';
import { useUserCurrencyList } from '@/lib/services/user.service';
import { Language } from '@/i18n/settings';
import { TokenAvatar } from '../common/TokenAvatar';
import { MultiChainSelector } from './MultiChainSelector';

type Props = {};

type HistoryProps = {
  address: string;
  tokenName: string;
  pairAddress: string;
  tokenIcon: string;
  chainIcon: string;
  chainId: string;
};

export function Navbar({}: Props) {
  const pathname = usePathname();
  const locale = pathname.split('/')[1];
  const { isLogin, openLoginModal } = useLoginRedirect();
  const t = useTranslations('Navigation');
  const h = useTranslations('HomePage.Trade');
  const defaultTokenAvatar = useDefaultTokenAvatar();
  const { data: userCurrencyList } = useUserCurrencyList();
  const walletsList = userCurrencyList?.userWalletGroups || [];
  const walletId = userCurrencyList?.userWalletGroups?.[0]?.walletGroupId;
  const [searchHistoryOpen, setSearchHistoryOpen] = useState(false);
  const { refetch: refetchSession, data: sessionData } = useSession();
  const [keyword, setKeyword] = useState<string>('');
  const searchParams = useSearchParams();
  const defaultChainId = searchParams.get('chainId') || checkChainId();
  const [chainId, setChainId] = useState<string>(defaultChainId);
  const { data: trendingSearchListData, refetch: refetchTrendingSearch } =
    useTrendingSearch(locale as Language, Number(chainId));
  const [isSearch, setIsSearch] = useState<boolean>(false);
  const {
    data: marketSearchListData,
    refetch: refetchMarketSearch,
    isLoading: isMarketSearchLoading,
  } = useMarketSearch(keyword, isSearch);
  const [historyList, setHistoryList] = useState<HistoryProps[]>([]);
  const router = useRouter();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const currentPath = pathname.split('/')[2];

  const { setColorMode } = useColorMode();

  const onLogin = async () => {
    refetchSession();
  };

  const onLogout = () => {
    clearToken();
    queryClient.clear();
    dispatch(resetStore());
    router.replace('/');
  };

  const toggleLocale = () => {
    const currentLocale = pathname.split('/')[1];
    const newLocale = currentLocale === 'en-US' ? 'zh-CN' : 'en-US';
    const newPath = pathname.replace(`/${currentLocale}`, `/${newLocale}`);
    router.push(newPath);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value == '') {
      setIsSearch(false);
    }
    setKeyword(event.target.value);
  };

  useEffect(() => {
    const list = getHistoryList();
    setHistoryList(list || []);
    window.addEventListener('click', (event: any) => {
      let classList = event.target.classList;
      if (!classList.value.includes('searchBox')) {
        setSearchHistoryOpen(false);
      }
    });
  }, []);

  const getHistoryList = () => {
    try {
      const history = global.localStorage.getItem('history');
      let list = [];
      if (history) {
        list = JSON.parse(history);
      }
      return list ?? [];
    } catch {
      return undefined;
    }
  };

  const onShowToken = (
    tokenName: string,
    address: string,
    pairAddress: string,
    tokenIcon: string,
    chainIcon: string,
    chainId: string,
  ) => {
    if (isSearch) {
      let data = [...historyList];
      let arr = data.filter((item: HistoryProps) => {
        return item.address == address;
      });
      if (arr.length == 0) {
        data.push({
          tokenName,
          address,
          pairAddress,
          tokenIcon,
          chainIcon,
          chainId,
        });
        global.localStorage.setItem('history', JSON.stringify(data));
      }
      setHistoryList(data);
    }
    const currentLocale = pathname.split('/')[1];
    let url = `/${currentLocale}/trade/${CHAIN_ID_TO_LOWER_NAME[chainId]}/${address}`;
    if (pairAddress) {
      url = `${url}?pairAddress=${pairAddress}`;
    }
    router.push(url);
    setSearchHistoryOpen(false);
  };

  const onHandleFavoriteToken = (data: MarketTrending, event: any) => {
    event?.stopPropagation();
    if (!isLogin) {
      openLoginModal();
      return;
    }
    let params: TokenFavorite = {
      pair_address: data?.pairAddress,
      favorite_type: data?.isFollowed ? 2 : 1,
      chain_id: data?.chainId,
    };
    postMarketTokenFavorite(params).then((res) => {
      refetchTrendingSearch();
      refetchMarketSearch();
    });
  };

  const onRenderList = () => {
    const tokens = isSearch
      ? marketSearchListData?.token
        ? [marketSearchListData.token]
        : [...(marketSearchListData?.list || [])]
      : [...(trendingSearchListData?.list || [])];
    if (isSearch && isMarketSearchLoading) {
      return (
        <VStack colorPalette="teal" pt={'100px'}>
          <Spinner color="text.primary" />
          <Text color="text.primary">Loading...</Text>
        </VStack>
      );
    } else {
      return (
        <Box className="searchBox">
          {tokens.map((item: MarketTrending, index: number) => (
            <Flex
              key={index}
              align="center"
              px={4}
              py={2}
              _hover={{ bg: 'bg.t2' }}
              cursor="pointer"
              className="searchBox"
              onClick={() => {
                onShowToken(
                  item?.tokenName,
                  item?.tokenAddress,
                  item?.pairAddress,
                  item.tokenIcon,
                  item.chainIcon,
                  item.chainId.toString(),
                );
              }}
            >
              <Flex flex="2" align="center" gap={2} className="searchBox">
                <Box
                  className="searchBox"
                  position={'relative'}
                  w={'34px'}
                  h={'34px'}
                >
                  <TokenAvatar
                    tokenAvatar={item?.tokenIcon}
                    chainAvatar={item?.chainIcon}
                  />
                </Box>
                <Box className="searchBox">
                  <Text className="searchBox">{item?.tokenName || ''}</Text>
                  <Text className="searchBox" fontSize="xs" color="text.t1">
                    {abbreviateHashString(item.tokenAddress)}
                  </Text>
                </Box>
              </Flex>
              <Box className="searchBox" flex="1.5" color="text.t1">
                {marketCapValueFormat(item?.mcap)}
              </Box>
              <Box className="searchBox" flex="1.5" color="text.t1">
                {formatPrice(Number(item?.price || 0))}
              </Box>
              <Box className="searchBox" flex="1.5">
                <Text color={item?.change24h >= 0 ? 'fixed.green' : 'red.500'}>
                  {formatPercent(item?.change24h)}
                </Text>
              </Box>
              <Box
                className="searchBox"
                flex="0.5"
                cursor={'pointer'}
                onClick={(e) => {
                  onHandleFavoriteToken(item, e);
                }}
                display={'flex'}
                justifyContent={'flex-end'}
                alignItems={'center'}
              >
                {item?.isFollowed ? (
                  <CollectionFilled fill="text.primary" />
                ) : (
                  <Collection />
                )}
              </Box>
            </Flex>
          ))}
          {tokens.length === 0 && (
            <Center w="100%" h="100%">
              <Text color="text.t2" fontSize="sm" mt={4}>
                {t('noResultsFound')}
              </Text>
            </Center>
          )}
        </Box>
      );
    }
  };

  const onClearHistory = () => {
    global.localStorage.removeItem('history');
    setHistoryList([]);
  };

  return (
    <Box bg="bg.t0" py={2} px={10}>
      <Flex align="center" justify="space-between">
        <Flex align="center" gap={'40px'}>
          <Box cursor={'pointer'}>
            <Logo />
          </Box>
          <Flex gap={'24px'} align="center">
            <Link
              href={`/${locale}`}
              onClick={() => {
                nProgress.start();
              }}
            >
              <Text
                fontSize={'16px'}
                color={
                  ['hot-list', 'pump'].includes(currentPath)
                    ? 'text.t1'
                    : 'text.t2'
                }
                fontWeight={
                  ['hot-list', 'pump'].includes(currentPath) ? 600 : 400
                }
                whiteSpace={'nowrap'}
              >
                {h('trending')}
              </Text>
            </Link>
            <Link
              href={`/${locale}/ai-earn`}
              onClick={() => {
                nProgress.start();
              }}
            >
              <Text
                fontSize={'16px'}
                color={
                  ['ai-earn'].includes(currentPath) ? 'text.t1' : 'text.t2'
                }
                fontWeight={['ai-earn'].includes(currentPath) ? 600 : 400}
                whiteSpace={'nowrap'}
              >
                AI Earn
              </Text>
            </Link>
            <Link
              href={`/${locale}/perpetual`}
              onClick={() => {
                nProgress.start();
              }}
            >
              <Text
                fontSize={'16px'}
                color={
                  ['perpetual'].includes(currentPath) ? 'text.t1' : 'text.t2'
                }
                fontWeight={['perpetual'].includes(currentPath) ? 600 : 400}
                whiteSpace={'nowrap'}
              >
                Perpetual
              </Text>
            </Link>
            {/* <Link
              href={`/${locale}/PumpX/ai-agent`}
              onClick={() => {
                nProgress.start();
              }}
            >
              <Text
                fontSize={'16px'}
                color={pathname.includes('ai-agent') ? 'text.t1' : 'text.t2'}
                fontWeight={pathname.includes('ai-agent') ? 600 : 400}
                whiteSpace={'nowrap'}
              >
                {h('agent')}
              </Text>
            </Link>

            <Link
              href={`/${locale}/PumpX/x-news`}
              onClick={() => {
                nProgress.start();
              }}
            >
              <Text
                fontSize={'16px'}
                color={pathname.includes('x-news') ? 'text.t1' : 'text.t2'}
                fontWeight={pathname.includes('x-news') ? 600 : 400}
                whiteSpace={'nowrap'}
              >
                {h('news')}
              </Text>
            </Link> */}
          </Flex>
          <Box position="relative" mx={4} className="searchBox" w={'470px'}>
            <Input
              rounded="full"
              placeholder={t('emailRegister.searchPlaceholder')}
              bg="bg.t2"
              border="none"
              color="text.t1"
              _placeholder={{ color: 'text.t3' }}
              _focus={{
                border: 'none',
                boxShadow: 'none',
              }}
              w={'470px'}
              pl={10}
              onClick={() => setSearchHistoryOpen(true)}
              value={keyword}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setIsSearch(true);
                }
              }}
              onInput={handleSearchChange}
              className="searchBox"
            />
            <Box
              position="absolute"
              left={3}
              top="50%"
              transform="translateY(-50%)"
              color="text.t3"
              onClick={() => {
                setIsSearch(true);
              }}
            >
              <Icon>
                <SearchIcon />
              </Icon>
            </Box>
            {searchHistoryOpen && (
              <Box
                borderRadius="12px"
                width="497px"
                height="480px"
                padding="16px"
                border="1px solid"
                borderColor="text.t4"
                bg="bg.t1"
                overflowY="auto"
                scrollbar="hidden"
                className="searchBox"
                position="absolute"
                left={0}
                top={12}
                zIndex={19}
              >
                {historyList.length > 0 && (
                  <Flex justify="space-between" mb={4} className="searchBox">
                    <Text fontSize="lg" fontWeight="bold" className="searchBox">
                      {t('searchHistory')}
                    </Text>
                    <IconButton
                      aria-label="Clear history"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        onClearHistory();
                      }}
                      className="searchBox"
                    >
                      <FaRegTrashAlt />
                    </IconButton>
                  </Flex>
                )}
                <Flex gap={2} flexWrap="wrap" mb={6} className="searchBox">
                  {historyList.map((tag) => (
                    <Button
                      key={tag.address}
                      size="sm"
                      variant="ghost"
                      _hover={{ bg: 'bg.t2' }}
                      padding="4px 8px"
                      borderRadius="20px"
                      bg="bg.t2"
                      className="searchBox"
                      onClick={() => {
                        onShowToken(
                          tag?.tokenName,
                          tag?.address,
                          tag?.pairAddress,
                          tag.tokenIcon,
                          tag.chainIcon,
                          tag.chainId,
                        );
                      }}
                    >
                      <Box
                        className="searchBox"
                        position={'relative'}
                        w={'16px'}
                        h={'16px'}
                      >
                        <Image
                          className="searchBox"
                          src={
                            tag.tokenIcon ? tag.tokenIcon : defaultTokenAvatar
                          }
                          alt="ETH"
                          w={'16px'}
                          h={'16px'}
                          borderRadius="full"
                        />
                        <Image
                          src={
                            tag.chainIcon ? tag.chainIcon : defaultTokenAvatar
                          }
                          position={'absolute'}
                          right={0}
                          bottom={0}
                          w={'6px'}
                          h={'6px'}
                          borderRadius="full"
                        />
                      </Box>
                      {tag.tokenName}
                    </Button>
                  ))}
                </Flex>
                <Box className="searchBox">
                  {!isSearch && (
                    <>
                      <Text
                        fontSize="lg"
                        fontWeight="bold"
                        mb={4}
                        className="searchBox"
                      >
                        🔥 {t('trending')}
                      </Text>
                      <Box mb={4}>
                        <MultiChainSelector
                          selectType={'TAB'}
                          value={chainId}
                          onChange={(chainId) => {
                            setChainId(chainId);
                          }}
                        />
                      </Box>
                    </>
                  )}

                  <Stack gap={2} className="searchBox">
                    <Flex
                      color="text.t2"
                      fontSize="sm"
                      px={4}
                      mb={2}
                      className="searchBox"
                    >
                      <Box flex="2" className="searchBox">
                        {t('token')}
                      </Box>
                      <Box flex="1.5" className="searchBox">
                        {t('liquidity')}
                      </Box>
                      <Box flex="1.5" className="searchBox">
                        {t('price')}
                      </Box>
                      <Box flex="1.5" className="searchBox">
                        {t('change')}
                      </Box>
                      <Box flex="0.5" className="searchBox">
                        {t('favorite')}
                      </Box>
                    </Flex>
                    {onRenderList()}
                  </Stack>
                </Box>
              </Box>
            )}
          </Box>
        </Flex>
        <Stack direction="row" gap={4}>
          {sessionData?.isLogin ? (
            <UserWalletMenu />
          ) : (
            <>
              {/* <EmailRegister onLogin={onLogin} /> */}
              <EmailLogin onLogin={onLogin} />
            </>
          )}
          {sessionData?.isLogin && <UserHomeMenu onDisconnect={onLogout} />}
          <ColorModeButton color={'text.t2'} />
          {/*<IconButton*/}
          {/*  aria-label="Language"*/}
          {/*  variant="ghost"*/}
          {/*  size="sm"*/}
          {/*  color="text.t2"*/}
          {/*  _hover={{*/}
          {/*    bg: 'bg.t1',*/}
          {/*  }}*/}
          {/*  onClick={toggleLocale}*/}
          {/*>*/}
          {/*  <LanguageIcon />*/}
          {/*</IconButton>*/}
          {/* <Link href={`/${locale}/download`} target="_blank" prefetch={false}>
            <ButtonUI
              aria-label="Download"
              color="rgba(6, 8, 11, 1)"
              h="auto"
              py={'6px'}
              px={4}
              rounded="full"
              bg="#ABEA45"
            >
              <Phone />
              {t('downloadApp')}
            </ButtonUI>
          </Link> */}
        </Stack>
      </Flex>
    </Box>
  );
}
